import type {ComponentPublicInstance, Ref} from 'vue';
import {nextTick, onBeforeUnmount, onMounted, readonly, ref} from 'vue';

interface UseContentOverflowOptions {
    /** The element you want to observe */
    root: Ref<HTMLElement | ComponentPublicInstance | undefined>;
    /**
     * Enable observing element at mount
     * @default true
     * */
    immediate?: boolean;
    /**
     * Show text ellipsis if overflowing
     * @default true
     * */
    ellipsis?: boolean;
}

/**
 * Detect if an element is overflowing. Ex: if the content is larger than the container.
 * This composable will apply by default a ellipsis to the overflowing content, but you can disable it to display your own overflow behaviour.
 *
 * @param options - Options
 *
 * @usage
 *
 * ```vue
 *
 * <template>
 *      <span ref='content'>Looooooong string</span>
 * </template>
 *
 * <script setup lang='ts'>
 * const content = ref<HtmlElement>();
 *
 * const {isOverflowing} = useContentOverflow({root: myRef});
 *
 * // Or in lazy mode
 *
 * const {isOverflowing, startDetecting, stopDetecting} = useContentOverflow({root: myRef, immediate: false});
 *
 * function doStuff() {
 *     startDetecting();
 * }
 * ```
 * </script>
 * ``
 *
 */
export function useContentOverflow({root, immediate = true, ellipsis = true}: UseContentOverflowOptions) {
    const isOverflowing = ref(false);
    const previousWidth = ref(0);

    const target = ref<HTMLElement>();

    function handleTruncation() {
        window.requestAnimationFrame(() => {
            const element = target.value;
            if (element && previousWidth.value !== element.offsetWidth) {
                const isElementTruncated = element.offsetWidth < element.scrollWidth;
                isOverflowing.value = isElementTruncated;
                previousWidth.value = element.offsetWidth;
            }
        });
    }

    let observer: ResizeObserver | undefined;

    function startObserving() {
        nextTick(() => {
            if (!root.value) {
                return;
            }

            if (root.value instanceof HTMLElement) {
                target.value = root.value;
            } else if (!Array.isArray(root.value.$el)) {
                target.value = root.value.$el as HTMLElement;
            }
            if (target.value) {
                target.value.style.overflow = 'hidden';
                target.value.style.whiteSpace = 'nowrap';
                if (ellipsis) {
                    target.value.style.textOverflow = 'ellipsis';
                }

                observer?.observe(target.value);
                handleTruncation();
            }
        });
    }

    function stopObserving() {
        observer?.disconnect();
    }
    onMounted(() => {
        observer = new ResizeObserver(handleTruncation);

        if (immediate) {
            startObserving();
        }
    });

    onBeforeUnmount(() => {
        stopObserving();
    });

    return {
        isOverflowing: readonly(isOverflowing),
        startObserving,
        stopObserving,
    };
}
